class GeratStoreLocator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.cacheElements();
    this.branches = this.readBranches();
    this.userLocation = null;
    this.activeBranchId = null;
    this.markers = new Map();

    this.populateStateFilter();
    this.bindEvents();
    this.render();
    this.trackEvent('gerat_locator_page_view', {
      branch_count: this.branches.length
    });
    this.initMap();
  }

  cacheElements() {
    this.searchInput = this.querySelector('[data-store-search]');
    this.stateFilter = this.querySelector('[data-state-filter]');
    this.locationButton = this.querySelector('[data-use-location]');
    this.clearLocationButton = this.querySelector('[data-clear-location]');
    this.status = this.querySelector('[data-status]');
    this.resultCount = this.querySelector('[data-result-count]');
    this.list = this.querySelector('[data-store-list]');
    this.emptyMessage = this.querySelector('[data-empty-message]');
    this.mapElement = this.querySelector('[data-map]');
  }

  readBranches() {
    const source = this.querySelector('[data-store-locator-branches]');

    if (!source) return [];

    try {
      return JSON.parse(source.textContent)
        .map((branch, index) => this.normalizeBranch(branch, index))
        .filter(branch => branch.active && (branch.name || branch.address));
    } catch (error) {
      this.setStatus('No pudimos leer las sucursales. Revisa la configuración de la sección.');
      return [];
    }
  }

  normalizeBranch(branch, index) {
    const lat = this.parseCoordinate(branch.lat);
    const lng = this.parseCoordinate(branch.lng);
    const address = this.cleanText(branch.address);
    const state = this.cleanText(branch.state);
    const city = this.cleanText(branch.city);
    const name = this.cleanText(branch.name) || address || `Sucursal ${index + 1}`;
    const generatedDirectionsUrl = this.buildDirectionsUrl({ lat, lng, address, name });
    const storedDirectionsUrl = this.safeUrl(branch.directionsUrl);

    return {
      id: this.cleanText(branch.id) || `branch-${index}`,
      name,
      state,
      city,
      address,
      phone: this.cleanText(branch.phone),
      whatsapp: this.cleanText(branch.whatsapp),
      hours: this.cleanText(branch.hours),
      lat,
      lng,
      order: this.parseOrder(branch.order),
      directionsUrl: generatedDirectionsUrl || storedDirectionsUrl,
      phoneUrl: this.buildPhoneUrl(branch.phone),
      whatsappUrl: this.buildWhatsAppUrl(branch.whatsapp),
      active: branch.active !== false && branch.active !== 'false',
      searchable: [name, state, city, address, branch.phone, branch.whatsapp, branch.hours].filter(Boolean).join(' ').toLowerCase()
    };
  }

  parseCoordinate(value) {
    if (value === null || value === undefined || value === '') return null;

    const number = parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(number) ? number : null;
  }

  parseOrder(value) {
    if (value === null || value === undefined || value === '') return null;

    const number = parseInt(value, 10);
    return Number.isFinite(number) ? number : null;
  }

  cleanText(value) {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  safeUrl(value) {
    const url = this.cleanText(value);
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '';
    } catch (error) {
      return '';
    }
  }

  buildDirectionsUrl(branch) {
    if (branch.lat !== null && branch.lng !== null) {
      return `https://www.google.com/maps/dir/?api=1&destination=${branch.lat},${branch.lng}`;
    }

    const destination = branch.address || branch.name;
    return destination ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}` : '';
  }

  buildPhoneUrl(phone) {
    const cleanPhone = this.cleanText(phone).replace(/[^\d+]/g, '');

    return cleanPhone ? `tel:${cleanPhone}` : '';
  }

  buildWhatsAppUrl(phone) {
    let cleanPhone = this.cleanText(phone).replace(/[^\d]/g, '');

    if (!cleanPhone) return '';
    if (cleanPhone.length === 10) cleanPhone = `52${cleanPhone}`;

    const message = encodeURIComponent(
      this.cleanText(this.dataset.whatsappMessage) || 'Hola, quiero información de esta sucursal Gerat.'
    );
    const isMobile = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);

    return isMobile
      ? `https://wa.me/${cleanPhone}?text=${message}`
      : `https://web.whatsapp.com/send?phone=${cleanPhone}&text=${message}`;
  }

  isAnalyticsEnabled() {
    return this.dataset.analyticsEnabled !== 'false';
  }

  shouldSendSearchTerms() {
    return this.dataset.analyticsSendSearchTerms !== 'false';
  }

  trackEvent(eventName, params = {}) {
    if (!this.isAnalyticsEnabled()) return;

    const payload = this.cleanAnalyticsPayload({
      event: eventName,
      event_category: 'store_locator',
      locator_section_id: this.dataset.sectionId || this.id || 'gerat_store_locator',
      page_path: window.location.pathname,
      page_title: document.title,
      ...params
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    try {
      if (window.Shopify?.analytics?.publish) {
        const { event, ...shopifyPayload } = payload;
        window.Shopify.analytics.publish(event, shopifyPayload);
      }
    } catch (error) {
      // Analytics should never block the store locator experience.
    }
  }

  cleanAnalyticsPayload(payload) {
    return Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
  }

  getBranchAnalytics(branch, extra = {}) {
    return {
      store_id: branch.id,
      store_name: branch.name,
      store_state: branch.state || 'Sin estado',
      store_city: branch.city || 'Sin ciudad',
      store_order: branch.order,
      ...extra
    };
  }

  trackBranchEvent(eventName, branch, extra = {}) {
    this.trackEvent(eventName, this.getBranchAnalytics(branch, extra));
  }

  queueSearchEvent() {
    window.clearTimeout(this.searchEventTimer);

    this.searchEventTimer = window.setTimeout(() => {
      const rawQuery = this.searchInput.value;
      const sanitizedTerm = this.sanitizeSearchTerm(rawQuery);
      const resultCount = this.getFilteredBranches().length;
      const searchKey = `${sanitizedTerm}|${resultCount}`;

      if (!sanitizedTerm || searchKey === this.lastTrackedSearch) return;

      this.lastTrackedSearch = searchKey;
      this.trackEvent('gerat_locator_search', {
        search_term: this.shouldSendSearchTerms() ? sanitizedTerm : undefined,
        search_length: this.cleanText(rawQuery).length,
        result_count: resultCount,
        selected_state: this.stateFilter.value || 'Todos los estados'
      });
    }, 700);
  }

  sanitizeSearchTerm(value) {
    return this.cleanText(value)
      .toLowerCase()
      .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '')
      .replace(/\+?\d[\d\s().-]{5,}\d/g, '')
      .replace(/\b\d{2,}\b/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80);
  }

  populateStateFilter() {
    const states = [...new Set(this.branches.map(branch => branch.state).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'es'));

    states.forEach(state => {
      const option = document.createElement('option');
      option.value = state;
      option.textContent = state;
      this.stateFilter.append(option);
    });
  }

  bindEvents() {
    this.searchInput.addEventListener('input', () => {
      this.render();
      this.queueSearchEvent();
    });

    this.stateFilter.addEventListener('change', () => {
      this.render();
      this.trackEvent('gerat_locator_filter_state', {
        selected_state: this.stateFilter.value || 'Todos los estados',
        result_count: this.getFilteredBranches().length
      });
    });

    this.locationButton.addEventListener('click', () => this.requestLocation());
    this.clearLocationButton.addEventListener('click', () => this.clearLocation());
  }

  getFilteredBranches() {
    const query = this.searchInput.value.trim().toLowerCase();
    const state = this.stateFilter.value;

    return this.branches
      .filter(branch => !state || branch.state === state)
      .filter(branch => !query || branch.searchable.includes(query))
      .map(branch => ({
        ...branch,
        distance: this.userLocation && branch.lat !== null && branch.lng !== null
          ? this.getDistance(this.userLocation, branch)
          : null
      }))
      .sort((a, b) => {
        if (a.distance !== null && b.distance !== null) return a.distance - b.distance;
        if (a.distance !== null) return -1;
        if (b.distance !== null) return 1;
        if (a.order !== null && b.order !== null && a.order !== b.order) return a.order - b.order;
        if (a.order !== null && b.order === null) return -1;
        if (a.order === null && b.order !== null) return 1;

        return [a.state, a.city, a.name].join(' ').localeCompare([b.state, b.city, b.name].join(' '), 'es');
      });
  }

  render() {
    const branches = this.getFilteredBranches();
    this.list.replaceChildren();
    this.emptyMessage.hidden = branches.length > 0;
    this.resultCount.textContent = `${branches.length} ${branches.length === 1 ? 'sucursal' : 'sucursales'}`;

    const groups = this.groupByState(branches);

    groups.forEach(group => {
      const groupElement = document.createElement('section');
      groupElement.className = 'gerat-store-locator__state-group';

      const title = document.createElement('h2');
      title.className = 'gerat-store-locator__state-title';
      title.textContent = group.state || 'Sin estado';
      groupElement.append(title);

      group.branches.forEach(branch => {
        groupElement.append(this.createBranchCard(branch));
      });

      this.list.append(groupElement);
    });

    this.updateMarkers(branches);
  }

  groupByState(branches) {
    const groups = new Map();

    branches.forEach(branch => {
      const state = branch.state || 'Sin estado';

      if (!groups.has(state)) {
        groups.set(state, []);
      }

      groups.get(state).push(branch);
    });

    return [...groups.entries()].map(([state, stateBranches]) => ({
      state,
      branches: stateBranches
    }));
  }

  createBranchCard(branch) {
    const card = document.createElement('article');
    card.className = 'gerat-store-locator__card';
    card.dataset.branchId = branch.id;
    card.tabIndex = 0;

    if (branch.id === this.activeBranchId) {
      card.classList.add('is-active');
    }

    const heading = document.createElement('div');
    heading.className = 'gerat-store-locator__card-heading';

    const title = document.createElement('h3');
    title.className = 'gerat-store-locator__card-title';
    title.textContent = branch.name;
    heading.append(title);

    if (branch.distance !== null) {
      const distance = document.createElement('span');
      distance.className = 'gerat-store-locator__distance';
      distance.textContent = `${branch.distance.toFixed(branch.distance < 10 ? 1 : 0)} km`;
      heading.append(distance);
    }

    card.append(heading);
    card.append(this.createMeta(branch));

    const actions = document.createElement('div');
    actions.className = 'gerat-store-locator__card-actions';

    if (branch.directionsUrl) {
      const directions = document.createElement('a');
      directions.className = 'gerat-store-locator__directions';
      directions.href = branch.directionsUrl;
      directions.target = '_blank';
      directions.rel = 'noopener noreferrer';
      directions.textContent = 'Cómo llegar';
      directions.addEventListener('click', () => this.trackBranchEvent('gerat_locator_get_directions', branch));
      actions.append(directions);
    }

    if (branch.phoneUrl) {
      const call = document.createElement('a');
      call.className = 'gerat-store-locator__call';
      call.href = branch.phoneUrl;
      call.textContent = 'Llamar';
      call.addEventListener('click', () => this.trackBranchEvent('gerat_locator_call', branch));
      actions.append(call);
    }

    if (branch.whatsappUrl) {
      const whatsapp = document.createElement('a');
      whatsapp.className = 'gerat-store-locator__whatsapp';
      whatsapp.href = branch.whatsappUrl;
      whatsapp.target = '_blank';
      whatsapp.rel = 'noopener noreferrer';
      whatsapp.textContent = 'WhatsApp';
      whatsapp.addEventListener('click', () => this.trackBranchEvent('gerat_locator_whatsapp', branch));
      actions.append(whatsapp);
    }

    card.append(actions);

    card.addEventListener('click', event => {
      if (event.target.closest('a')) return;
      this.selectBranch(branch.id, 'card');
    });

    card.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.selectBranch(branch.id, 'card_keyboard');
      }
    });

    return card;
  }

  createMeta(branch) {
    const meta = document.createElement('div');
    meta.className = 'gerat-store-locator__meta';

    this.appendMetaLine(meta, 'Dirección', [branch.address, branch.city].filter(Boolean).join(', '));
    this.appendMetaLine(meta, 'Teléfono', branch.phone);
    this.appendMetaLine(meta, 'Horario', branch.hours);

    return meta;
  }

  appendMetaLine(container, label, value) {
    if (!value) return;

    const line = document.createElement('span');
    const strong = document.createElement('strong');
    strong.textContent = `${label}: `;
    line.append(strong, document.createTextNode(value));
    container.append(line);
  }

  requestLocation() {
    this.trackEvent('gerat_locator_use_location', {
      location_status: 'requested'
    });

    if (!navigator.geolocation) {
      this.setStatus('Tu navegador no permite usar ubicación actual.');
      this.trackEvent('gerat_locator_location_error', {
        location_status: 'unsupported'
      });
      return;
    }

    this.locationButton.disabled = true;
    this.setStatus('Buscando tu ubicación...');

    navigator.geolocation.getCurrentPosition(
      position => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        this.clearLocationButton.hidden = false;
        this.locationButton.disabled = false;
        this.setStatus('Ordenamos las sucursales por cercanía a tu ubicación.');
        this.render();
        this.updateUserMarker();
        this.trackEvent('gerat_locator_location_success', {
          location_status: 'success',
          result_count: this.getFilteredBranches().length
        });
      },
      () => {
        this.locationButton.disabled = false;
        this.setStatus('No pudimos obtener tu ubicación. Puedes buscar por estado o ciudad.');
        this.trackEvent('gerat_locator_location_error', {
          location_status: 'denied_or_unavailable'
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  }

  clearLocation() {
    this.userLocation = null;
    this.clearLocationButton.hidden = true;
    this.setStatus('');
    this.trackEvent('gerat_locator_clear_location');

    if (this.userMarker) {
      this.userMarker.setMap(null);
      this.userMarker = null;
    }

    this.render();
  }

  getDistance(origin, branch) {
    const earthRadiusKm = 6371;
    const deltaLat = this.toRadians(branch.lat - origin.lat);
    const deltaLng = this.toRadians(branch.lng - origin.lng);
    const startLat = this.toRadians(origin.lat);
    const endLat = this.toRadians(branch.lat);
    const a = Math.sin(deltaLat / 2) ** 2
      + Math.cos(startLat) * Math.cos(endLat) * Math.sin(deltaLng / 2) ** 2;

    return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  toRadians(value) {
    return value * Math.PI / 180;
  }

  setStatus(message) {
    this.status.textContent = message;
  }

  async initMap() {
    const apiKey = this.dataset.googleMapsApiKey;

    if (!apiKey) {
      this.setStatus('El listado está listo. Falta agregar la API key para activar el mapa.');
      return;
    }

    try {
      await GeratStoreLocator.loadGoogleMaps(apiKey, this.dataset.googleMapsMapId);

      const center = {
        lat: this.parseCoordinate(this.dataset.defaultLat) || 23.6345,
        lng: this.parseCoordinate(this.dataset.defaultLng) || -102.5528
      };

      this.mapElement.replaceChildren();
      this.map = new google.maps.Map(this.mapElement, {
        center,
        zoom: Number(this.dataset.defaultZoom) || 5,
        mapId: this.dataset.googleMapsMapId || undefined,
        clickableIcons: false,
        fullscreenControl: true,
        mapTypeControl: false,
        streetViewControl: false
      });

      this.infoWindow = new google.maps.InfoWindow();
      this.updateMarkers(this.getFilteredBranches());
    } catch (error) {
      this.setStatus('No pudimos cargar Google Maps. Revisa la API key y sus restricciones.');
    }
  }

  static loadGoogleMaps(apiKey, mapId) {
    if (window.google?.maps) return Promise.resolve();
    if (GeratStoreLocator.googleMapsPromise) return GeratStoreLocator.googleMapsPromise;

    GeratStoreLocator.googleMapsPromise = new Promise((resolve, reject) => {
      const callback = `geratStoreLocatorLoaded${Date.now()}`;
      const script = document.createElement('script');
      const params = new URLSearchParams({
        key: apiKey,
        callback,
        v: 'weekly'
      });

      if (mapId) {
        params.set('map_ids', mapId);
      }

      window[callback] = () => {
        delete window[callback];
        resolve();
      };

      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;
      script.onerror = reject;
      document.head.append(script);
    });

    return GeratStoreLocator.googleMapsPromise;
  }

  updateMarkers(branches) {
    if (!this.map || !window.google?.maps) return;

    this.markers.forEach(marker => marker.setMap(null));
    this.markers.clear();

    const bounds = new google.maps.LatLngBounds();
    const markerPositions = [];
    let hasBounds = false;

    branches.forEach(branch => {
      if (branch.lat === null || branch.lng === null) return;

      const position = { lat: branch.lat, lng: branch.lng };
      const marker = new google.maps.Marker({
        map: this.map,
        position,
        title: branch.name
      });

      marker.addListener('click', () => this.selectBranch(branch.id, 'map_marker'));
      this.markers.set(branch.id, marker);
      bounds.extend(marker.getPosition());
      markerPositions.push(position);
      hasBounds = true;
    });

    if (this.userLocation) {
      this.updateUserMarker();
      bounds.extend(this.userLocation);
      hasBounds = true;
    }

    if (hasBounds) {
      if (markerPositions.length === 1 && !this.userLocation) {
        this.map.setCenter(markerPositions[0]);
        this.map.setZoom(this.getSingleBranchZoom());
      } else {
        this.map.fitBounds(bounds, 70);
        this.capAutoZoom();
      }
    }
  }

  getSingleBranchZoom() {
    return Number(this.dataset.singleBranchZoom) || 14;
  }

  getMaxAutoZoom() {
    return Number(this.dataset.maxAutoZoom) || 14;
  }

  capAutoZoom() {
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      const maxZoom = this.getMaxAutoZoom();

      if (this.map.getZoom() > maxZoom) {
        this.map.setZoom(maxZoom);
      }
    });
  }

  updateUserMarker() {
    if (!this.map || !this.userLocation || !window.google?.maps) return;

    if (this.userMarker) {
      this.userMarker.setMap(null);
    }

    this.userMarker = new google.maps.Marker({
      map: this.map,
      position: this.userLocation,
      title: 'Tu ubicación',
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#171717',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3
      }
    });
  }

  selectBranch(branchId, source = 'unknown') {
    this.activeBranchId = branchId;
    this.querySelectorAll('.gerat-store-locator__card').forEach(card => {
      card.classList.toggle('is-active', card.dataset.branchId === branchId);
    });

    const branch = this.branches.find(item => item.id === branchId);
    const marker = this.markers.get(branchId);

    if (!branch) return;

    this.trackBranchEvent('gerat_locator_select_store', branch, {
      selection_source: source
    });

    if (!marker || !this.map) return;

    this.map.panTo(marker.getPosition());
    this.map.setZoom(Math.max(this.map.getZoom(), this.getSingleBranchZoom()));

    if (this.infoWindow) {
      this.infoWindow.setContent(this.getInfoWindowContent(branch));
      this.infoWindow.open(this.map, marker);
    }
  }

  getInfoWindowContent(branch) {
    const address = [branch.address, branch.city].filter(Boolean).join(', ');

    return `
      <div class="gerat-store-locator__info-window">
        <strong>${this.escapeHtml(branch.name)}</strong>
        ${address ? `<span>${this.escapeHtml(address)}</span>` : ''}
        ${branch.phone ? `<span>${this.escapeHtml(branch.phone)}</span>` : ''}
        ${branch.directionsUrl ? `<a href="${this.escapeAttribute(branch.directionsUrl)}" target="_blank" rel="noopener noreferrer">Cómo llegar</a>` : ''}
      </div>
    `;
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, character => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[character]);
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/`/g, '&#96;');
  }
}

customElements.define('gerat-store-locator', GeratStoreLocator);
